export type CheckoutTaskType = 'info' | 'contract' | 'id_document'

export type CheckoutSignatureFieldType = 'drawn' | 'typed_name' | 'date' | 'checkbox'

export interface CheckoutSignatureField {
  id: string
  type: CheckoutSignatureFieldType
  label: string
  required: boolean
}

export interface CheckoutContractConfig {
  /** HTML contract template shown to the customer */
  contentHtml: string
  /** Require scrolling to the end before signing */
  requireScrollToEnd: boolean
  signatureFields: CheckoutSignatureField[]
  /** Uploaded company / locator signature image URL (from admin) */
  companySignatureUrl?: string
}

export interface CheckoutTask {
  id: string
  /** Short label in the checkout stepper */
  label: string
  /** Card title on the step */
  title: string
  description: string
  type: CheckoutTaskType
  enabled: boolean
  /** Attach this task's documents (contract PDF / ID photos) to order emails */
  includeInEmail: boolean
  /** Present when type === 'contract' */
  contract?: CheckoutContractConfig
}

export const CHECKOUT_TASK_TYPE_OPTIONS: { label: string; value: CheckoutTaskType }[] = [
  { label: 'Informații / confirmare', value: 'info' },
  { label: 'Contract (semnătură)', value: 'contract' },
  { label: 'Documente identitate', value: 'id_document' },
]

export const CHECKOUT_SIGNATURE_FIELD_TYPE_OPTIONS: {
  label: string
  value: CheckoutSignatureFieldType
}[] = [
  { label: 'Semnătură desenată', value: 'drawn' },
  { label: 'Checkbox acord', value: 'checkbox' },
]

export const DEFAULT_CHECKOUT_TASKS: CheckoutTask[] = []

export const DEFAULT_CONTRACT_SIGNATURE_FIELDS: CheckoutSignatureField[] = [
  {
    id: 'sig-drawn',
    type: 'drawn',
    label: 'Semnătură',
    required: true,
  },
  {
    id: 'sig-agree',
    type: 'checkbox',
    label: 'Am citit și sunt de acord cu termenii contractului',
    required: true,
  },
]

function normalizeType(raw: unknown): CheckoutTaskType {
  if (raw === 'contract' || raw === 'id_document' || raw === 'info') return raw
  return 'info'
}

export function defaultIncludeInEmail(type: CheckoutTaskType): boolean {
  return type === 'contract'
}

function normalizeIncludeInEmail(raw: unknown, type: CheckoutTaskType): boolean {
  if (raw === true || raw === 'true' || raw === 1) return true
  if (raw === false || raw === 'false' || raw === 0) return false
  return defaultIncludeInEmail(type)
}

function normalizeSignatureFieldType(raw: unknown): CheckoutSignatureFieldType {
  if (raw === 'drawn' || raw === 'typed_name' || raw === 'date' || raw === 'checkbox') return raw
  return 'drawn'
}

function newId(prefix: string): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}-${crypto.randomUUID()}`
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

export function createSignatureField(
  partial?: Partial<CheckoutSignatureField>,
): CheckoutSignatureField {
  const type = normalizeSignatureFieldType(partial?.type)
  const defaults: Record<CheckoutSignatureFieldType, string> = {
    drawn: 'Semnătură',
    typed_name: 'Nume complet',
    date: 'Data',
    checkbox: 'Sunt de acord',
  }

  return {
    id: partial?.id?.trim() || newId('sig'),
    type,
    label: partial?.label?.trim() || defaults[type],
    required: partial?.required !== false,
  }
}

export function createContractConfig(
  partial?: Partial<CheckoutContractConfig>,
): CheckoutContractConfig {
  const fields = Array.isArray(partial?.signatureFields)
    ? partial.signatureFields
      .map((field) => createSignatureField(field))
      // Numele și data vin din CI / comanda — nu mai apar pe formularul de semnare.
      .filter((field) => field.type !== 'typed_name' && field.type !== 'date')
    : DEFAULT_CONTRACT_SIGNATURE_FIELDS.map((field) => ({ ...field }))

  return {
    contentHtml: typeof partial?.contentHtml === 'string' ? partial.contentHtml : '',
    requireScrollToEnd: partial?.requireScrollToEnd !== false,
    signatureFields: fields.length
      ? fields
      : DEFAULT_CONTRACT_SIGNATURE_FIELDS.map((field) => ({ ...field })),
    companySignatureUrl: typeof partial?.companySignatureUrl === 'string'
      ? partial.companySignatureUrl.trim() || undefined
      : undefined,
  }
}

export function createCheckoutTask(partial?: Partial<CheckoutTask>): CheckoutTask {
  const type = normalizeType(partial?.type)
  const task: CheckoutTask = {
    id: partial?.id || newId('task'),
    label: partial?.label?.trim() || '',
    title: partial?.title?.trim() || '',
    description: partial?.description?.trim() || '',
    type,
    enabled: partial?.enabled !== false,
    includeInEmail: normalizeIncludeInEmail(partial?.includeInEmail, type),
  }

  if (type === 'contract') {
    task.contract = createContractConfig(partial?.contract)
  }

  return task
}

export function parseCheckoutTasks(
  raw?: string | null,
  fallback: CheckoutTask[] = DEFAULT_CHECKOUT_TASKS,
): CheckoutTask[] {
  if (!raw) return fallback.map((task) => createCheckoutTask(task))
  try {
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return fallback.map((task) => createCheckoutTask(task))

    return parsed
      .map((item, index) => {
        if (!item || typeof item !== 'object') return null
        const row = item as Record<string, unknown>
        const label = String(row.label ?? '').trim()
        const title = String(row.title ?? label).trim()
        if (!label && !title) return null

        const type = normalizeType(row.type)
        const contractRaw = row.contract && typeof row.contract === 'object'
          ? row.contract as Partial<CheckoutContractConfig>
          : undefined
        const storedId = typeof row.id === 'string' && row.id.trim() ? row.id.trim() : ''

        return createCheckoutTask({
          id: storedId || `task-${index}`,
          label: label || title,
          title: title || label,
          description: String(row.description ?? '').trim(),
          type,
          enabled: row.enabled !== false && row.enabled !== 'false' && row.enabled !== 0,
          includeInEmail: row.includeInEmail,
          contract: type === 'contract' ? contractRaw : undefined,
        })
      })
      .filter((task): task is CheckoutTask => Boolean(task))
      .map((task, index, all) => {
        const duplicate = all.findIndex((other) => other.id === task.id) !== index
        if (!duplicate) return task
        return { ...task, id: `${task.id}-${index}` }
      })
  } catch {
    return fallback.map((task) => createCheckoutTask(task))
  }
}

export function enabledCheckoutTasks(tasks: CheckoutTask[]): CheckoutTask[] {
  return tasks.filter((task) => task.enabled && task.label.trim())
}
